import React, { lazy, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import Header from './components/header';
import Navigation from './components/navigation';
import Portfolio from './components/portfolio';
import LazyRoute from './components/LazyRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Footer from './components/footer';

// Route chunks are kept as named loaders so they can be warmed ahead of time.
// Without that, the first visit to any route hit a Suspense fallback mid
// transition — which is where the loading spinner came from.
const routeChunks = {
  videos: () => import('./components/videos'),
  about: () => import('./components/about'),
  contact: () => import('./components/contact'),
  artwork: () => import('./components/artwork'),
  services: () => import('./components/Services'),
  productVisualization: () => import('./components/ProductVisualization'),
  architecturalRendering: () => import('./components/ArchitecturalRendering'),
  gameAssets: () => import('./components/GameAssets'),
  animation3D: () => import('./components/Animation3D'),
  blog: () => import('./components/Blog'),
  blogPost: () => import('./components/BlogPost'),
  notFound: () => import('./components/NotFound'),
};

const Videos = lazy(routeChunks.videos);
const About = lazy(routeChunks.about);
const Contact = lazy(routeChunks.contact);
const Artwork = lazy(routeChunks.artwork);
const Services = lazy(routeChunks.services);
const ProductVisualization = lazy(routeChunks.productVisualization);
const ArchitecturalRendering = lazy(routeChunks.architecturalRendering);
const GameAssets = lazy(routeChunks.gameAssets);
const Animation3D = lazy(routeChunks.animation3D);
const Blog = lazy(routeChunks.blog);
const BlogPost = lazy(routeChunks.blogPost);
const NotFound = lazy(routeChunks.notFound);

// Warm every route chunk after first paint, so moving between pages never waits
// on a network round trip for JavaScript.
//
// One chunk per idle callback, never all of them in one pass: warming all twelve
// together parsed as a single ~590ms long task, which landed on whatever
// navigation happened to be in flight and was felt as a stutter. Sequencing
// turns that into twelve short tasks the browser can interleave.
const usePrefetchRoutes = () => {
  useEffect(() => {
    const queue = Object.values(routeChunks);
    let cancelled = false;
    let handle = null;

    const schedule = (fn) => {
      if (typeof window.requestIdleCallback === 'function') {
        return window.requestIdleCallback(fn, { timeout: 2000 });
      }
      return setTimeout(fn, 60);
    };
    const unschedule = (h) => {
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(h);
      else clearTimeout(h);
    };

    const next = () => {
      if (cancelled) return;
      const load = queue.shift();
      if (!load) return;
      load().catch(() => {});
      handle = schedule(next);
    };
    handle = schedule(next);

    return () => { cancelled = true; if (handle !== null) unschedule(handle); };
  }, []);
};

// Every page is a single screen and carries its own closing line, so the tall
// site footer would push each one past the fold. The blog post is the one page
// that is meant to scroll — it keeps the footer.
const SCROLLING_ROUTES = /^\/blog\/.+/;
const SiteFooter = () => {
  const { pathname } = useLocation();
  return SCROLLING_ROUTES.test(pathname) ? <Footer /> : null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navTypeRef = useRef(navigationType);
  navTypeRef.current = navigationType;

  return (
    <AnimatePresence
      mode="wait"
      initial={false}
      onExitComplete={() => {
        if (navTypeRef.current !== 'POP') window.scrollTo(0, 0);
      }}
    >
      <Routes location={location} key={location.pathname}>
        <Route path="/videos" element={<LazyRoute><Videos /></LazyRoute>} />
        <Route path="/about" element={<LazyRoute><About /></LazyRoute>} />
        <Route path="/contact" element={<LazyRoute><Contact /></LazyRoute>} />
        <Route path="/services" element={<LazyRoute><Services /></LazyRoute>} />
        <Route path="/product-visualization" element={<LazyRoute><ProductVisualization /></LazyRoute>} />
        <Route path="/architectural-rendering" element={<LazyRoute><ArchitecturalRendering /></LazyRoute>} />
        <Route path="/game-assets" element={<LazyRoute><GameAssets /></LazyRoute>} />
        <Route path="/3d-animation" element={<LazyRoute><Animation3D /></LazyRoute>} />
        <Route path="/artworks/:id" element={<LazyRoute><Artwork /></LazyRoute>} />
        <Route path="/blog" element={<LazyRoute><Blog /></LazyRoute>} />
        <Route path="/blog/:slug" element={<LazyRoute><BlogPost /></LazyRoute>} />
        <Route path="/" element={<Portfolio />} />
        <Route path="*" element={<LazyRoute><NotFound /></LazyRoute>} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => {
  usePrefetchRoutes();
  return (
    // The CSS prefers-reduced-motion guard in index.css only neuters CSS
    // transitions and animations. Framer Motion drives inline styles through
    // the Web Animations API, which that rule cannot touch — so it kept
    // animating for people who had asked their OS to stop. reducedMotion="user"
    // makes Framer honour the same preference.
    <MotionConfig reducedMotion="user">
    <Router>
      <div className="flex flex-col min-h-screen">
        <Header />
        <Navigation />
        <main className="flex-grow pt-[var(--nav-h)]">
          <ErrorBoundary>
            <AnimatedRoutes />
          </ErrorBoundary>
        </main>
        <SiteFooter />
      </div>
    </Router>
    </MotionConfig>
  );
};

export default App;
